import { translateErrorCode, useTranslation } from '@jq4race/i18n';
import type { BatchCheckInItemResult } from '@jq4race/shared';
import { Badge, Button } from '@jq4race/ui';
import { CheckCircle2, Trash2, XCircle } from 'lucide-react';

export interface QueuedItem {
  clientId: string;
  plate: string;
  ticketType: 'sms' | 'qr';
  /** Set when this item is retained after a partial-failure batch submit (R4.4 reconcile). */
  error?: string;
}

interface FlashBatchQueueProps {
  items: QueuedItem[];
  onRemove: (clientId: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  submitError: string | null;
  results: BatchCheckInItemResult[] | null;
  onReset: () => void;
}

/** Accumulated batch of arrivals awaiting submission, plus per-item results (R4.4). */
export function FlashBatchQueue({
  items,
  onRemove,
  onSubmit,
  submitting,
  submitError,
  results,
  onReset,
}: FlashBatchQueueProps) {
  const { t } = useTranslation('flash');

  if (results) {
    const successCount = results.filter((r) => r.success).length;
    return (
      <div className="space-y-3">
        <p className="jq-body-sm text-foreground">
          {t('batchQueue.resultsSummary', { success: successCount, total: results.length })}
        </p>
        <ul className="space-y-2">
          {results.map((result) => (
            <li
              key={result.index}
              className="flex items-start gap-2 rounded-sm border border-border bg-secondary px-3 py-2 text-[13px]"
            >
              {result.success ? (
                <CheckCircle2
                  className="mt-0.5 size-4 shrink-0 text-jq-success"
                  strokeWidth={1.5}
                />
              ) : (
                <XCircle className="mt-0.5 size-4 shrink-0 text-destructive" strokeWidth={1.5} />
              )}
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{result.plate}</span>
                {result.success ? (
                  <>
                    <span className="text-muted-foreground">
                      {' '}
                      {t('batchQueue.resultTicket', { number: result.ticketNumber })}
                      {result.photosPending ? ` ${t('batchQueue.photosPending')}` : ''}
                    </span>
                    {result.smsStatus === 'sent' && (
                      <span className="text-muted-foreground"> · {t('batchQueue.smsSent')}</span>
                    )}
                    {result.smsStatus === 'failed' && (
                      <span className="text-jq-warning"> · {t('batchQueue.smsFailed')}</span>
                    )}
                  </>
                ) : (
                  <span className="text-destructive">
                    {' '}
                    —{' '}
                    {result.error
                      ? translateErrorCode(result.error.code, t, {
                          details: { field: result.error.field },
                        })
                      : null}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        <Button type="button" size="touch" className="w-full" onClick={onReset}>
          {t('batchQueue.startNewBatch')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="jq-body-sm font-semibold text-foreground">{t('batchQueue.title')}</p>
        <div className="flex items-center gap-3">
          {items.length > 0 && (
            <button
              type="button"
              onClick={onReset}
              disabled={submitting}
              className="jq-caption text-muted-foreground underline"
            >
              {t('batchQueue.clearQueue')}
            </button>
          )}
          <Badge variant={items.length > 0 ? 'default' : 'secondary'}>{items.length}</Badge>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="jq-caption text-muted-foreground">{t('batchQueue.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.clientId}
              className="flex items-center justify-between gap-2 rounded-sm border border-border bg-secondary px-3 py-2 text-[13px]"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{item.plate}</span>
                  <span className="text-muted-foreground uppercase">{item.ticketType}</span>
                </div>
                {item.error && <p className="text-destructive">{item.error}</p>}
              </div>
              <button
                type="button"
                aria-label={t('batchQueue.removeAria', { plate: item.plate })}
                onClick={() => onRemove(item.clientId)}
                disabled={submitting}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="size-4" strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {submitError && (
        <p role="alert" className="text-[13px] text-destructive">
          {submitError}
        </p>
      )}

      <Button
        type="button"
        size="touch"
        className="w-full"
        disabled={items.length === 0 || submitting}
        onClick={onSubmit}
      >
        {submitting ? t('batchQueue.submitting') : t('batchQueue.submit', { count: items.length })}
      </Button>
    </div>
  );
}
